import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../common/LanguageSwitcher';
import UserAvatar from '../common/UserAvatar';
import { RoostingBrandLockup } from '../we';
import { useAuth } from '../../context/AuthContext';
import { messageApi } from '../../utils/api';

const UNREAD_REFRESH_MS = 30_000;

const Header: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }
    let cancelled = false;
    const refresh = async () => {
      const resp = await messageApi.getUnreadCount();
      if (!cancelled && resp.success && resp.data) {
        setUnreadCount(resp.data.count);
      }
    };
    refresh();
    const interval = setInterval(refresh, UNREAD_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isAuthenticated, location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  const navLinkClasses = (path: string) =>
    `px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
      isActive(path)
        ? 'text-primary-600 bg-primary-50'
        : 'text-neutral-700 hover:text-primary-600 hover:bg-neutral-100'
    }`;

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-neutral-100 shadow-sm">
      <nav className="container-app" aria-label="Main navigation">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <RoostingBrandLockup size={28} wordmarkText={t('common.appName')} />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <Link to="/" className={navLinkClasses('/')}>{t('nav.home')}</Link>
            {user?.role !== 'nanny' && (
              <Link to="/search" className={navLinkClasses('/search')}>{t('nav.findNannies')}</Link>
            )}
            <Link to="/credentials" className={navLinkClasses('/credentials')}>{t('nav.credentials')}</Link>
            {isAuthenticated && (
              <>
                <Link to="/dashboard" className={navLinkClasses('/dashboard')}>{t('common.dashboard')}</Link>
                <Link to="/messages" className={`${navLinkClasses('/messages')} relative`}>
                  {t('common.messages')}
                  {unreadCount > 0 && (
                    <span
                      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-primary-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                      aria-label={t('messaging.unreadAria', { count: unreadCount })}
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
              </>
            )}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                <Link
                  to="/profile"
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-neutral-700 hover:text-primary-600 rounded-lg"
                >
                  <UserAvatar
                    photoUrl={user?.profilePhotoUrl}
                    emoji={user?.emojiAvatar}
                    firstName={user?.firstName}
                    lastName={user?.lastName}
                    size="sm"
                  />
                  <span>{user?.firstName}</span>
                </Link>
                <button onClick={logout} className="btn-ghost text-sm">
                  {t('common.logOut')}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" className="btn-ghost text-sm">{t('common.logIn')}</Link>
                <Link to="/register" className="btn-primary text-sm !py-2 !px-4">{t('common.signUp')}</Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-neutral-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? t('accessibility.closeMenu') : t('accessibility.openMenu')}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-neutral-100 animate-fade-in">
            <div className="flex flex-col gap-1">
              <Link to="/" className={navLinkClasses('/')} onClick={() => setMobileMenuOpen(false)}>{t('nav.home')}</Link>
              {user?.role !== 'nanny' && (
                <Link to="/search" className={navLinkClasses('/search')} onClick={() => setMobileMenuOpen(false)}>{t('nav.findNannies')}</Link>
              )}
              <Link to="/credentials" className={navLinkClasses('/credentials')} onClick={() => setMobileMenuOpen(false)}>{t('nav.credentials')}</Link>
              {isAuthenticated ? (
                <>
                  <Link to="/dashboard" className={navLinkClasses('/dashboard')} onClick={() => setMobileMenuOpen(false)}>{t('common.dashboard')}</Link>
                  <Link to="/messages" className={navLinkClasses('/messages')} onClick={() => setMobileMenuOpen(false)}>{t('common.messages')}</Link>
                  <Link to="/profile" className={navLinkClasses('/profile')} onClick={() => setMobileMenuOpen(false)}>{t('common.profile')}</Link>
                  <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="btn-ghost text-left">{t('common.logOut')}</button>
                </>
              ) : (
                <div className="flex gap-2 mt-3 pt-3 border-t border-neutral-100">
                  <Link to="/login" className="btn-secondary flex-1 text-center text-sm" onClick={() => setMobileMenuOpen(false)}>{t('common.logIn')}</Link>
                  <Link to="/register" className="btn-primary flex-1 text-center text-sm" onClick={() => setMobileMenuOpen(false)}>{t('common.signUp')}</Link>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-neutral-100">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
